import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, MicOff, Users, ArrowLeft, Loader2, PhoneOff, AlertCircle, Send, MessageCircle, X, VolumeX, Volume2, Plus, ShoppingBag, Bell, CheckCircle2, Sparkles, Store, Heart, Share2, Zap, Lock, Mail, Eye, EyeOff, LogIn, ChevronUp, ChevronDown } from 'lucide-react';
import { getUserId, getUserName, liveAPI, getProducts, API_BASE, loginUser } from '../api/client';
import { useAuth } from '../context/AuthContext';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';
import Hls from 'hls.js';
import { getCurrentLanguage, isRTL } from '../utils/translations';
import '../live-room.css';

const ROOM_I18N = {
    ar: {
        joinTitle: "انضمام للبث المباشر (LUXVONO LIVE)",
        joinDesc: "أدخل اسمك أو كنيتك للدخول والمشاركة في البث والتفاعل المباشر.",
        loginTitle: "تسجيل الدخول للبث المباشر (LUXVONO LIVE)",
        loginDesc: "سجّل الدخول بحسابك الحقيقي للتفاعل مع البائع، كتابة التعليقات، والشراء الفوري بعروض حصرية.",
        emailOrPhone: "البريد الإلكتروني أو اسم المستخدم",
        emailPlaceholder: "name@example.com",
        password: "كلمة المرور",
        passwordPlaceholder: "••••••••",
        loginBtn: "تسجيل الدخول والمتابعة 👑",
        loggingIn: "جاري التحقق والدخول...",
        dontHaveAccount: "ليس لديك حساب؟",
        createAccount: "إنشاء حساب جديد",
        continueAsViewer: "المتابعة كزائر (مشاهدة فقط بدون تعليق)",
        loginRequiredForChat: "يرجى تسجيل الدخول بحسابك الحقيقي للتعليق في البث 🔒",
        namePlaceholder: "اسمك الكريم...",
        cancel: "إلغاء",
        enterLive: "دخول البث",
        connectedLive: "★ البث المباشر الفاخر متصل وتفاعلي",
        unmute: "انقر لتشغيل الصوت 🔊",
        liveDot: "مباشر",
        leave: "خروج من البث",
        pinnedExclusive: "★ معروض الآن في البث ⚡",
        productDetails: "تفاصيل المنتج",
        addToCart: "إضافة للسلة 🛒",
        instantBuy: "شراء فوري ⚡",
        you: "أنت",
        chatPlaceholder: "أرسل تعليقاً أو استفساراً في البث...",
        send: "إرسال",
        like: "إعجاب",
        gift: "إهداء",
        crown: "تاج",
        share: "مشاركة",
        sound: "الصوت",
        bag: "شنطة البث",
        bagTitle: "حقيبة منتجات البث المباشر (TikTok Shop)",
        follow: "+ متابعة",
        following: "✓ متابع",
        addedToCart: "🛒 تمت إضافة المنتج إلى السلة بنجاح!",
        linkCopied: "🔗 تم نسخ رابط البث المباشر بنجاح!",
        sentDiamond: "💎 أرسلت ماسة فاخرة لمقدم البث!",
        sentCrown: "👑 أرسلت تاج الملوك للبث المباشر!",
        featuredNow: "معروض الآن في البث ⚡",
        streamEnded: "👑 تم إنهاء البث المباشر - شكراً لحضوركم وتفاعلكم الراقي معنا!",
        streamEndedTitle: "👑 تم إغلاق البث المباشر",
        streamEndedSubtitle: "أغلق البائع جلسة البث المباشر. يمكنك تفعيل التذكير الفوري لتصلك إشعارات حصرية فور بدء البث القادم!",
        remindMeBtn: "🔔 أغلق البث - اضغط للتذكير",
        reminderActive: "✓ تم تفعيل التذكير بنجاح! سنقوم بإشعارك فور انطلاق البث القادم 🔔",
        reminderSubmitting: "جاري تفعيل التذكير...",
        browseStore: "تصفح متجر البائع",
        backToHome: "العودة للرئيسية"
    },
    en: {
        joinTitle: "Join Live Showcase (LUXVONO LIVE)",
        joinDesc: "Enter your name to participate in the live interactive broadcast.",
        loginTitle: "Sign In to Live Showcase (LUXVONO LIVE)",
        loginDesc: "Sign in with your authentic account to participate in live chat, send gifts, and access exclusive flash deals.",
        emailOrPhone: "Email or Username",
        emailPlaceholder: "name@example.com",
        password: "Password",
        passwordPlaceholder: "••••••••",
        loginBtn: "Sign In & Enter Live 👑",
        loggingIn: "Verifying credentials...",
        dontHaveAccount: "Don't have an account?",
        createAccount: "Create New Account",
        continueAsViewer: "Continue as Guest (View Only)",
        loginRequiredForChat: "Please sign in with your real account to participate in live chat 🔒",
        namePlaceholder: "Your full name...",
        cancel: "Cancel",
        enterLive: "Enter Live",
        connectedLive: "★ Sovereign Live Stream Connected & Interactive",
        unmute: "Click to Unmute 🔊",
        liveDot: "LIVE",
        leave: "Leave Stream",
        pinnedExclusive: "★ Featured Live Now ⚡",
        productDetails: "Product Details",
        addToCart: "Add to Bag 🛒",
        instantBuy: "Instant Buy ⚡",
        you: "You",
        chatPlaceholder: "Send a message in live stream...",
        send: "Send",
        like: "Like",
        gift: "Gift",
        crown: "Crown",
        share: "Share",
        sound: "Sound",
        bag: "Live Bag",
        bagTitle: "Live Showcase Catalog (TikTok Shop)",
        follow: "+ Follow",
        following: "✓ Following",
        addedToCart: "🛒 Added product to bag successfully!",
        linkCopied: "🔗 Stream link copied to clipboard!",
        sentDiamond: "💎 Sent luxury diamond to host!",
        sentCrown: "👑 Sent royal crown to stream!",
        featuredNow: "Featured Live Now ⚡",
        streamEnded: "👑 Live stream has concluded. Thank you for joining us!",
        streamEndedTitle: "👑 Live Stream Concluded",
        streamEndedSubtitle: "The host has closed this live session. Tap below to activate instant notifications for the next stream!",
        remindMeBtn: "🔔 Stream Ended - Click for Reminder",
        reminderActive: "✓ Reminder Activated! We'll notify you the moment the next live starts 🔔",
        reminderSubmitting: "Activating reminder...",
        browseStore: "Browse Seller Store",
        backToHome: "Back to Home"
    },
    fr: {
        joinTitle: "Rejoindre le Direct (LUXVONO LIVE)",
        joinDesc: "Entrez votre nom pour participer à la diffusion interactive en direct.",
        namePlaceholder: "Votre nom...",
        cancel: "Annuler",
        enterLive: "Rejoindre le Direct",
        connectedLive: "★ Diffusion en direct connectée et interactive",
        unmute: "Cliquer pour réactiver le son 🔊",
        liveDot: "EN DIRECT",
        leave: "Quitter le direct",
        pinnedExclusive: "★ Pièce exclusive présentée en direct",
        productDetails: "Détails du produit",
        addToCart: "Ajouter au panier",
        you: "Vous",
        chatPlaceholder: "Envoyer un message en direct...",
        send: "Envoyer",
        streamEnded: "👑 La diffusion est terminée. Merci de votre présence !"
    },
    de: {
        joinTitle: "Live-Showcase beitreten (LUXVONO LIVE)",
        joinDesc: "Geben Sie Ihren Namen ein, um am interaktiven Live-Stream teilzunehmen.",
        namePlaceholder: "Ihr Name...",
        cancel: "Abbrechen",
        enterLive: "Live betreten",
        connectedLive: "★ Live-Stream verbunden & interaktiv",
        unmute: "Klicken zum Aktivieren des Tons 🔊",
        liveDot: "LIVE",
        leave: "Stream verlassen",
        pinnedExclusive: "★ Exklusives Live-Präsentationsstück",
        productDetails: "Produktdetails",
        addToCart: "In den Warenkorb",
        you: "Sie",
        chatPlaceholder: "Nachricht im Live-Stream senden...",
        send: "Senden",
        streamEnded: "👑 Der Live-Stream ist beendet. Vielen Dank für Ihre Teilnahme!"
    },
    es: {
        joinTitle: "Unirse a la Transmisión (LUXVONO LIVE)",
        joinDesc: "Introduzca su nombre para participar e interactuar en vivo.",
        namePlaceholder: "Su nombre...",
        cancel: "Cancelar",
        enterLive: "Entrar al Directo",
        connectedLive: "★ Transmisión en vivo conectada e interactiva",
        unmute: "Haga clic para activar el sonido 🔊",
        liveDot: "EN VIVO",
        leave: "Salir de la transmisión",
        pinnedExclusive: "★ Pieza destacada y exclusiva del directo",
        productDetails: "Detalles del producto",
        addToCart: "Añadir a la cesta",
        you: "Tú",
        chatPlaceholder: "Escribe un mensaje en directo...",
        send: "Enviar",
        streamEnded: "👑 La transmisión en vivo ha finalizado. ¡Gracias por acompañarnos!"
    },
    it: {
        joinTitle: "Partecipa alla Diretta (LUXVONO LIVE)",
        joinDesc: "Inserisci il tuo nome per partecipare alla diretta interattiva.",
        namePlaceholder: "Il tuo nome...",
        cancel: "Annulla",
        enterLive: "Entra in Diretta",
        connectedLive: "★ Diretta streaming connessa e interattiva",
        unmute: "Clicca per attivare l'audio 🔊",
        liveDot: "IN DIRETTA",
        leave: "Esci dalla diretta",
        pinnedExclusive: "★ Prodotto in evidenza esclusivo",
        productDetails: "Dettagli prodotto",
        addToCart: "Aggiungi al carrello",
        you: "Tu",
        chatPlaceholder: "Invia un messaggio in diretta...",
        send: "Invia",
        streamEnded: "👑 La diretta streaming è terminata. Grazie per essere stati con noi!"
    },
    ru: {
        joinTitle: "Присоединиться к эфиру (LUXVONO LIVE)",
        joinDesc: "Введите ваше имя для участия в интерактивном прямом эфире.",
        namePlaceholder: "Ваше имя...",
        cancel: "Отмена",
        enterLive: "Войти в эфир",
        connectedLive: "★ Прямой эфир подключен и интерактивен",
        unmute: "Нажмите для включения звука 🔊",
        liveDot: "ЭФИР",
        leave: "Покинуть эфир",
        pinnedExclusive: "★ Эксклюзивный лот прямого эфира",
        productDetails: "Подробнее о товаре",
        addToCart: "В корзину",
        you: "Вы",
        chatPlaceholder: "Отправить сообщение в эфир...",
        send: "Отправить",
        streamEnded: "👑 Прямой эфир завершен. Благодарим за ваше присутствие!"
    },
    tr: {
        joinTitle: "Canlı Yayına Katıl (LUXVONO LIVE)",
        joinDesc: "Etkileşimli canlı yayına katılmak için adınızı girin.",
        namePlaceholder: "Adınız...",
        cancel: "İptal",
        enterLive: "Yayına Katıl",
        connectedLive: "★ Canlı yayın bağlantısı aktif ve etkileşimli",
        unmute: "Sesi açmak için tıklayın 🔊",
        liveDot: "CANLI",
        leave: "Yayından Ayrıl",
        pinnedExclusive: "★ Canlı yayına özel ayrıcalıklı parça",
        productDetails: "Ürün Detayları",
        addToCart: "Sepete Ekle",
        you: "Sen",
        chatPlaceholder: "Canlı yayına mesaj gönderin...",
        send: "Gönder",
        streamEnded: "👑 Canlı yayın sona erdi. Katılımınız için teşekkür ederiz!"
    },
    zh: {
        joinTitle: "进入实况直播间 (LUXVONO LIVE)",
        joinDesc: "请输入您的尊称，即刻加入实况奢华直播与大师实时互动。",
        namePlaceholder: "您的尊称...",
        cancel: "取消",
        enterLive: "进入直播间",
        connectedLive: "★ 皇家至尊实况直播已连接",
        unmute: "点击开启原声 🔊",
        liveDot: "直播中",
        leave: "退出直播间",
        pinnedExclusive: "★ 本场实况独家推荐至尊藏品",
        productDetails: "臻品详情",
        addToCart: "加入购物袋",
        you: "您",
        chatPlaceholder: "向直播间发送互动留言...",
        send: "发送",
        streamEnded: "👑 实况品鉴直播已圆满结束，感谢您的尊贵出席！"
    },
    ja: {
        joinTitle: "ライブ配信に参加 (LUXVONO LIVE)",
        joinDesc: "リアルタイム配信に参加するためのお名前を入力してください。",
        namePlaceholder: "お名前...",
        cancel: "キャンセル",
        enterLive: "配信ルームに入る",
        connectedLive: "★ ロイヤル・ライブ配信接続中",
        unmute: "クリックして音声をオン 🔊",
        liveDot: "LIVE",
        leave: "退出する",
        pinnedExclusive: "★ ライブ配信限定の注目アイテム",
        productDetails: "商品詳細",
        addToCart: "バッグに追加",
        you: "あなた",
        chatPlaceholder: "ライブ配信にメッセージを送信...",
        send: "送信",
        streamEnded: "👑 ライブ配信は終了いたしました。ご参加ありがとうございました！"
    },
    ko: {
        joinTitle: "라이브 쇼케이스 입장 (LUXVONO LIVE)",
        joinDesc: "실시간 대화형 라이브 방송에 참여하려면 이름을 입력하세요.",
        namePlaceholder: "성함...",
        cancel: "취소",
        enterLive: "방송 입장",
        connectedLive: "★ 로열 라이브 스트림 연결됨",
        unmute: "음소거 해제 클릭 🔊",
        liveDot: "라이브",
        leave: "방송 나가기",
        pinnedExclusive: "★ 라이브 방송 단독 추천 상품",
        productDetails: "상품 상세 정보",
        addToCart: "쇼핑백에 담기",
        you: "나",
        chatPlaceholder: "라이브 방송에 메시지 남기기...",
        send: "전송",
        streamEnded: "👑 라이브 방송이 종료되었습니다. 함께해 주셔서 감사합니다!"
    },
    so: {
        joinTitle: "Ku biir Baahinta Tooska ah (LUXVONO LIVE)",
        joinDesc: "Gali magacaaga si aad uga qayb qaadato baahinta tooska ah.",
        namePlaceholder: "Magacaaga...",
        cancel: "Ka noqo",
        enterLive: "Gal Baahinta",
        connectedLive: "★ Baahinta tooska ah waa shaqaynaysaa",
        unmute: "Guji si aad codka u shido 🔊",
        liveDot: "TOOS",
        leave: "Ka bax",
        pinnedExclusive: "★ Alaab gaar ah oo lagu soo bandhigay baahinta",
        productDetails: "Faahfaahinta alaabta",
        addToCart: "Ku dar Gaariga",
        you: "Adiga",
        chatPlaceholder: "Farriin u dir baahinta tooska ah...",
        send: "Dir",
        streamEnded: "👑 Baahintii tooska ahayd way dhammaatay. Waad ku mahadsan tahay joogitaankaaga!"
    },
    am: {
        joinTitle: "የቀጥታ ስርጭቱን ይቀላቀሉ (LUXVONO LIVE)",
        joinDesc: "በቀጥታ ስርጭቱ ላይ ለመሳተፍ ስምዎን ያስገቡ።",
        namePlaceholder: "ስምዎ...",
        cancel: "ይቅር",
        enterLive: "ወደ ስርጭቱ ይግቡ",
        connectedLive: "★ የቀጥታ ስርጭቱ ተገናኝቷል",
        unmute: "ድምጽ ለማብራት ጠቅ ያድርጉ 🔊",
        liveDot: "ቀጥታ",
        leave: "ከስርጭት ውጣ",
        pinnedExclusive: "★ በቀጥታ ስርጭት የቀረበ ልዩ እቃ",
        productDetails: "የእቃ ዝርዝሮች",
        addToCart: "ወደ ጋሪ ጨምር",
        you: "እርስዎ",
        chatPlaceholder: "በቀጥታ ስርጭቱ መልእክት ይላኩ...",
        send: "ላክ",
        streamEnded: "👑 የቀጥታ ስርጭቱ ተጠናቋል። ስለተሳተፉ እናመሰግናለን!"
    },
    gdq: {
        joinTitle: "انضمام للبث المباشر (LUXVONO LIVE)",
        joinDesc: "أدخل اسمك للدخول والمشاركة في البث المباشر باللسان المهري.",
        namePlaceholder: "اسمك الكريم...",
        cancel: "إلغاء",
        enterLive: "دخول البث",
        connectedLive: "★ البث المباشر الملكي متصل وتفاعلي",
        unmute: "انقر لتشغيل الصوت 🔊",
        liveDot: "مباشر",
        leave: "خروج من البث",
        pinnedExclusive: "★ مقتنى فاخر وحصري في البث",
        productDetails: "تفاصيل المقتنى",
        addToCart: "إضافة للسلة",
        you: "أنت",
        chatPlaceholder: "أرسل رسالة في البث المباشر...",
        send: "إرسال",
        streamEnded: "👑 تم إنهاء البث المباشر - شكراً لحضوركم وتفاعلكم الراقي معنا!"
    }
};

const ICE_SERVERS = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
    ]
};

const RemoteVideoPlayer = ({ stream, isMuted }) => {
    const videoRef = useRef(null);
    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(e => console.warn('Remote video playback warning:', e));
        }
    }, [stream]);

    return (
        <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={isMuted}
            className="lux-live-video-element"
        />
    );
};

export default function LiveSalesRoomPlayer({
    roomId,
    roomTitle,
    allRooms = [],
    currentRoomIndex = 0,
    onSwitchRoom,
    onLeave,
    onAddToCart,
    onBuyNow
}) {
    const navigate = useNavigate();
    const currentLang = getCurrentLanguage() || 'ar';
    const rtl = isRTL();
    const roomStrings = ROOM_I18N[currentLang] || ROOM_I18N['en'] || ROOM_I18N['ar'];
    const [participants, setParticipants] = useState([]);
    const [room, setRoom] = useState(null);
    const [discoveredRooms, setDiscoveredRooms] = useState([]);
    const stompClientRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [myState, setMyState] = useState(null);
    const [pinnedProduct, setPinnedProduct] = useState(null);

    // TikTok Vertical Swipe Gesture State (Up / Down between active live broadcasts)
    const touchStartYRef = useRef(null);
    const touchStartXRef = useRef(null);
    const lastWheelSwipeRef = useRef(0);
    const [swipeOffsetY, setSwipeOffsetY] = useState(0);
    const [isSwipingAnim, setIsSwipingAnim] = useState(false);

    // WebRTC Mesh state
    const localStreamRef = useRef(null);
    const peersRef = useRef(new Map());
    const [mediaStreams, setMediaStreams] = useState(new Map());
    const iceQueuesRef = useRef(new Map());
    const [requestStatus, setRequestStatus] = useState('none');
    const [isCoHostInvitesOpen, setIsCoHostInvitesOpen] = useState(false);

    // Chat State
    const [messages, setMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const chatEndRef = useRef(null);

    // Host Video & Sound State
    const hostVideoRef = useRef(null);
    const hostHlsRef = useRef(null);
    const [isSoundMuted, setIsSoundMuted] = useState(true);

    // TikTok Live Commerce States
    const [likesCount, setLikesCount] = useState(0);
    const [floatingHearts, setFloatingHearts] = useState([]);
    const [catalogProducts, setCatalogProducts] = useState([]);
    const [isCatalogDrawerOpen, setIsCatalogDrawerOpen] = useState(false);
    const [isFollowing, setIsFollowing] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);
    const toastTimeoutRef = useRef(null);
    const lastTapTimeRef = useRef(0);

    // Real Flash Sale State (Only shown when broadcasted by seller)
    const [flashSale, setFlashSale] = useState(null);

    // Live countdown effect for real flash sale
    useEffect(() => {
        if (!flashSale || !flashSale.isActive) return;
        const timerInterval = setInterval(() => {
            setFlashSale(prev => {
                if (!prev || !prev.isActive) return null;
                const newSec = (prev.timeRemainingSeconds ?? 180) - 1;
                if (newSec <= 0) return null;
                const min = Math.floor(newSec / 60);
                const sec = newSec % 60;
                return {
                    ...prev,
                    timeRemainingSeconds: newSec,
                    timer: `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
                };
            });
        }, 1000);
        return () => clearInterval(timerInterval);
    }, [flashSale?.isActive]);

    const showToast = (msg) => {
        if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
        setToastMessage(msg);
        toastTimeoutRef.current = setTimeout(() => setToastMessage(null), 2500);
    };

    const triggerHeart = (e = null, specificEmoji = null, broadcast = true) => {
        const emojis = ['❤️', '💖', '💎', '👑', '🔥', '✨', '🌹'];
        const chosenEmoji = specificEmoji || emojis[Math.floor(Math.random() * emojis.length)];

        let startX = 0;
        let startY = 100;
        if (e && e.clientX) {
            const rect = e.currentTarget?.getBoundingClientRect?.() || { left: 0, width: window.innerWidth };
            startX = e.clientX - rect.left - 20;
            startY = Math.max(80, window.innerHeight - e.clientY - 20);
        } else {
            const basePercent = rtl ? 0.22 : 0.78;
            const randomOffset = (Math.random() - 0.5) * 80;
            startX = (window.innerWidth * basePercent) + randomOffset;
            startY = 90 + Math.random() * 40;
        }

        const heartId = Date.now() + Math.random();
        const sway1 = (Math.random() * 40 - 20) + 'px';
        const sway2 = (Math.random() * 50 - 25) + 'px';
        const rot1 = (Math.random() * 30 - 15) + 'deg';
        const rot2 = (Math.random() * 30 - 15) + 'deg';
        const size = 26 + Math.floor(Math.random() * 12);

        setFloatingHearts(prev => [...prev.slice(-18), {
            id: heartId,
            emoji: chosenEmoji,
            x: Math.max(12, Math.min(window.innerWidth - 64, startX)),
            bottom: startY,
            sway1,
            sway2,
            rot1,
            rot2,
            size
        }]);

        setTimeout(() => {
            setFloatingHearts(prev => prev.filter(h => h.id !== heartId));
        }, 2600);

        setLikesCount(prev => prev + 1);

        if (broadcast && stompClientRef.current?.connected) {
            stompClientRef.current.send(`/app/live/${roomId}/broadcast`, {}, JSON.stringify({
                type: 'REACTION',
                senderId: myPeerId,
                senderName: myUserName,
                payload: chosenEmoji
            }));
        }
    };

    const handleToggleFollow = () => {
        const next = !isFollowing;
        setIsFollowing(next);
        try {
            const hostId = room?.hostId || (participants.find(p => p.role === 'HOST')?.userId) || 1;
            localStorage.setItem(`lux_follow_host_${hostId}`, String(next));
        } catch (_) {}
        showToast(next ? (roomStrings.following || '★ تم متابعة مقدم البث بنجاح!') : 'تم إلغاء المتابعة');
    };

    const handleShare = async () => {
        const shareData = {
            title: room?.title || 'LUXVONO VIP Live',
            text: 'شاهد البث المباشر الحصري وتسوق أرقى المنتجات الفاخرة على لوكسفونو!',
            url: window.location.href
        };
        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (_) {}
        } else {
            try {
                await navigator.clipboard.writeText(window.location.href);
                showToast(roomStrings.linkCopied || '🔗 تم نسخ رابط البث المباشر بنجاح!');
            } catch (_) {
                showToast('🔗 ' + window.location.href);
            }
        }
    };

    const handleAddToCart = (product) => {
        if (!product) return;
        if (onAddToCart) onAddToCart(product);
        triggerHeart(null, '💎', true);
        showToast(roomStrings.addedToCart || '🛒 تمت إضافة المنتج إلى السلة بنجاح!');
    };

    const handleBuyNow = async (product) => {
        if (!product) return;
        triggerHeart(null, '👑', true);
        if (onBuyNow) {
            try {
                await onBuyNow(product);
            } catch (e) {
                if (onAddToCart) onAddToCart(product);
                navigate('/cart');
            }
        } else {
            if (onAddToCart) onAddToCart(product);
            navigate('/cart');
        }
    };

    const handleVideoClick = (e) => {
        const now = Date.now();
        if (now - lastTapTimeRef.current < 360) {
            triggerHeart(e, null, true);
        }
        lastTapTimeRef.current = now;
    };

    // Auth & Real User Profile State
    const { user, isAuthenticated, login } = useAuth();
    const isUserLoggedIn = Boolean(isAuthenticated || localStorage.getItem('customer_token'));
    const initialName = user?.fullName || user?.firstName || getUserName() || (currentLang === 'ar' ? 'زائر لوكسفونو' : 'LUXVONO Guest');
    const [myUserName, setMyUserName] = useState(initialName);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(!isUserLoggedIn);
    const myUserId = user?.id || user?.userId || getUserId();
    const myPeerId = myUserId || (Date.now() % 1000000);

    // Live In-Stream Login Form States
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState('');

    // Keep myUserName in sync if AuthContext loads/updates user
    useEffect(() => {
        if (user) {
            const resolvedName = user.fullName || user.firstName || user.name || getUserName();
            if (resolvedName) {
                setMyUserName(resolvedName);
                setIsAuthModalOpen(false);
            }
        }
    }, [user]);

    const handleRealLogin = async (e) => {
        if (e) e.preventDefault();
        if (!loginEmail.trim() || !loginPassword) {
            setLoginError(currentLang === 'ar' ? 'يرجى إدخال البريد الإلكتروني وكلمة المرور' : 'Please enter your email and password');
            return;
        }
        setLoginLoading(true);
        setLoginError('');
        try {
            const payload = await loginUser({ email: loginEmail.trim(), password: loginPassword });
            if (payload && (payload.accessToken || payload.token)) {
                const loggedInUser = login(payload);
                const displayName = loggedInUser?.fullName || loggedInUser?.firstName || payload.user?.firstName || payload.user?.name || loginEmail.trim().split('@')[0];
                setMyUserName(displayName);
                setIsAuthModalOpen(false);
                showToast(currentLang === 'ar' ? `مرحباً بك ${displayName} في البث المباشر 👑` : `Welcome ${displayName} to Live Stream 👑`);
            } else {
                setLoginError(currentLang === 'ar' ? 'فشل تسجيل الدخول. يرجى التحقق من صحة البيانات.' : 'Login failed. Please verify credentials.');
            }
        } catch (err) {
            console.error('Live stream login error:', err);
            const serverMsg = err.response?.data?.message || err.response?.data?.error;
            setLoginError(serverMsg || (currentLang === 'ar' ? 'بيانات الدخول غير صحيحة. يرجى التأكد من البريد وكلمة المرور.' : 'Invalid credentials. Please try again.'));
        } finally {
            setLoginLoading(false);
        }
    };

    const handleContinueAsGuest = () => {
        setMyUserName(currentLang === 'ar' ? 'زائر (مشاهد فقط)' : 'Guest Viewer');
        setIsAuthModalOpen(false);
        showToast(currentLang === 'ar' ? 'أنت تشاهد البث كزائر 👁️. سجّل دخولك للتعليق والشراء.' : 'Viewing as guest 👁️. Sign in to chat or purchase.');
    };

    // Stream Ended & Reminder State
    const [isStreamEnded, setIsStreamEnded] = useState(false);
    const [isReminderSet, setIsReminderSet] = useState(() => {
        try {
            const stored = localStorage.getItem(`luxvono_live_reminder_${roomId}`);
            return stored === 'true';
        } catch (_) {
            return false;
        }
    });
    const [reminderSubmitting, setReminderSubmitting] = useState(false);

    const handleSetReminder = async () => {
        if (isReminderSet) return;
        setReminderSubmitting(true);
        try {
            const hostId = room?.hostId || (participants.find(p => p.role === 'HOST')?.userId) || 1;
            await liveAPI.subscribeReminder(roomId, hostId);
            localStorage.setItem(`luxvono_live_reminder_${roomId}`, 'true');
            setIsReminderSet(true);
        } catch (e) {
            console.error('Failed to set reminder:', e);
            localStorage.setItem(`luxvono_live_reminder_${roomId}`, 'true');
            setIsReminderSet(true);
        } finally {
            setReminderSubmitting(false);
        }
    };

    // Screen Wake Lock
    useEffect(() => {
        let wakeLock = null;
        const requestWakeLock = async () => {
            try {
                if ('wakeLock' in navigator) {
                    wakeLock = await navigator.wakeLock.request('screen');
                }
            } catch (err) {
                console.warn('Wake Lock warning:', err.message);
            }
        };
        requestWakeLock();

        const handleVisibilityChange = () => {
            if (wakeLock !== null && document.visibilityState === 'visible') {
                requestWakeLock();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (wakeLock !== null) {
                wakeLock.release().catch(() => {});
            }
        };
    }, []);

    useEffect(() => {
        setMessages([]);
        setIsStreamEnded(false);
        initRoom();
        return () => cleanup();
    }, [roomId]);

    const effectiveRooms = (Array.isArray(allRooms) && allRooms.length > 0) ? allRooms : discoveredRooms;
    const displayRoomIndex = Math.max(
        0,
        effectiveRooms.findIndex(r => String(r.id) === String(roomId)) !== -1
            ? effectiveRooms.findIndex(r => String(r.id) === String(roomId))
            : currentRoomIndex
    );

    const triggerVerticalRoomSwipe = async (direction = 'next') => {
        if (isSwipingAnim) return;
        setIsSwipingAnim(true);
        setSwipeOffsetY(direction === 'next' ? -60 : 60);

        const finishReset = () => {
            setTimeout(() => {
                setSwipeOffsetY(0);
                setIsSwipingAnim(false);
            }, 220);
        };

        if (typeof onSwitchRoom === 'function') {
            await onSwitchRoom(direction, (status, nextRoom, nextIdx, total) => {
                if (status === 'switched' && nextRoom) {
                    const hostLabel = nextRoom.title || nextRoom.sellerName || nextRoom.hostName || 'بث مباشر';
                    showToast(
                        currentLang === 'ar'
                            ? `📡 البث (${nextIdx + 1}/${total}): ${hostLabel}`
                            : `📡 Live (${nextIdx + 1}/${total}): ${hostLabel}`
                    );
                } else if (status === 'single') {
                    showToast(
                        currentLang === 'ar'
                            ? '📡 هذا هو البث المباشر الوحيد النشط حالياً — اسحب للأعلى أو الأسفل فور دخول بث آخر'
                            : '📡 Only 1 live stream is currently active — swipe up/down when another host goes live'
                    );
                }
            });
            finishReset();
            return;
        }

        // Standalone fallback if onSwitchRoom was not passed
        let list = effectiveRooms;
        if (list.length <= 1) {
            const fetched = await liveAPI.getActiveRooms().catch(() => []);
            if (Array.isArray(fetched) && fetched.length > 0) {
                setDiscoveredRooms(fetched);
                list = fetched;
            }
        }
        if (list.length <= 1) {
            showToast(
                currentLang === 'ar'
                    ? '📡 هذا هو البث المباشر الوحيد النشط حالياً — اسحب للأعلى أو الأسفل فور دخول بث آخر'
                    : '📡 Only 1 live stream is currently active'
            );
            finishReset();
            return;
        }
        const currIdx = Math.max(0, list.findIndex(r => String(r.id) === String(roomId)));
        const nextIdx = direction === 'prev'
            ? (currIdx - 1 + list.length) % list.length
            : (currIdx + 1) % list.length;
        const target = list[nextIdx];
        if (target && String(target.id) !== String(roomId)) {
            navigate(`/live/${target.id}`, { replace: true });
            showToast(
                currentLang === 'ar'
                    ? `📡 البث (${nextIdx + 1}/${list.length}): ${target.title || 'بث مباشر'}`
                    : `📡 Live (${nextIdx + 1}/${list.length}): ${target.title || 'Live Stream'}`
            );
        }
        finishReset();
    };

    const handleStageTouchStart = (e) => {
        if (isCatalogDrawerOpen || isAuthModalOpen) return;
        const touch = e.touches?.[0];
        if (!touch) return;
        touchStartYRef.current = touch.clientY;
        touchStartXRef.current = touch.clientX;
    };

    const handleStageTouchMove = (e) => {
        if (touchStartYRef.current === null || isCatalogDrawerOpen || isAuthModalOpen) return;
        const touch = e.touches?.[0];
        if (!touch) return;
        const deltaY = touch.clientY - touchStartYRef.current;
        const deltaX = Math.abs(touch.clientX - (touchStartXRef.current || 0));
        if (Math.abs(deltaY) > deltaX && Math.abs(deltaY) > 12) {
            setSwipeOffsetY(Math.max(-90, Math.min(90, deltaY * 0.35)));
        }
    };

    const handleStageTouchEnd = (e) => {
        if (touchStartYRef.current === null) return;
        const touch = e.changedTouches?.[0];
        const endY = touch ? touch.clientY : touchStartYRef.current;
        const endX = touch ? touch.clientX : (touchStartXRef.current || 0);
        const deltaY = endY - touchStartYRef.current;
        const deltaX = Math.abs(endX - (touchStartXRef.current || 0));
        touchStartYRef.current = null;
        touchStartXRef.current = null;

        if (Math.abs(deltaY) >= 55 && Math.abs(deltaY) > deltaX * 1.2) {
            if (deltaY < 0) {
                // Swiped UP -> Next Live Stream (TikTok style)
                triggerVerticalRoomSwipe('next');
            } else {
                // Swiped DOWN -> Previous Live Stream (TikTok style)
                triggerVerticalRoomSwipe('prev');
            }
        } else {
            setSwipeOffsetY(0);
        }
    };

    const handleStageWheel = (e) => {
        if (isCatalogDrawerOpen || isAuthModalOpen) return;
        const now = Date.now();
        if (now - lastWheelSwipeRef.current < 700) return;
        if (Math.abs(e.deltaY) >= 38) {
            lastWheelSwipeRef.current = now;
            if (e.deltaY > 0) {
                triggerVerticalRoomSwipe('next');
            } else {
                triggerVerticalRoomSwipe('prev');
            }
        }
    };

    useEffect(() => {
        const onKeyDown = (e) => {
            if (isCatalogDrawerOpen || isAuthModalOpen) return;
            const tag = (e.target?.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                triggerVerticalRoomSwipe('next');
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                triggerVerticalRoomSwipe('prev');
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isCatalogDrawerOpen, isAuthModalOpen, roomId, effectiveRooms.length]);

    const initRoom = async () => {
        try {
            const activeRooms = await liveAPI.getActiveRooms().catch(() => []);
            if (Array.isArray(activeRooms) && activeRooms.length > 0) {
                setDiscoveredRooms(activeRooms);
            }
            const currentRoom = (activeRooms || []).find(r => String(r.id) === String(roomId));
            if (currentRoom) {
                setRoom(currentRoom);
                if (currentRoom.likesCount != null) {
                    setLikesCount(Number(currentRoom.likesCount) || 0);
                }
            } else {
                setRoom({ id: roomId, title: roomTitle || 'LUXVONO Live Room', type: 'LIVE' });
            }

            // Load products for TikTok shop showcase
            let productsList = currentRoom?.products || [];
            if (!productsList || productsList.length === 0) {
                try {
                    const prodData = await getProducts(0, 10);
                    productsList = prodData?.content || prodData || [];
                } catch (_) {}
            }
            if (productsList && productsList.length > 0) {
                setCatalogProducts(productsList);
                setPinnedProduct(prev => prev || productsList[0]);
            }

            const hostId = currentRoom?.hostId || 1;
            try {
                const isF = localStorage.getItem(`lux_follow_host_${hostId}`);
                if (isF === 'true') setIsFollowing(true);
            } catch (_) {}

            const me = await liveAPI.joinRoom(roomId, myUserName, false).catch(() => ({
                id: myPeerId,
                userId: myPeerId,
                userName: myUserName,
                role: 'GUEST',
                muted: true
            }));
            setMyState(me);

            const parts = await liveAPI.getParticipants(roomId).catch(() => []);
            setParticipants(parts.length > 0 ? parts : [me]);

            connectSTOMP();
        } catch (e) {
            console.error('Room init fallback:', e);
            connectSTOMP();
        }
    };

    // HLS Stream Setup (Mobile & Desktop with proper DOM properties & error recovery)
    useEffect(() => {
        if (!room || !hostVideoRef.current) return;

        const streamUrl = room.streamUrl || (room.streamKey ? `${API_BASE || 'https://api.luxvono.com'}/media/hls/${room.streamKey}.m3u8` : null);
        if (!streamUrl) return;

        const video = hostVideoRef.current;
        video.muted = isSoundMuted;
        video.defaultMuted = isSoundMuted;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('x5-playsinline', '');

        if (Hls.isSupported()) {
            if (hostHlsRef.current) hostHlsRef.current.destroy();
            const hls = new Hls({
                liveSyncDurationCount: 3,
                liveMaxLatencyDurationCount: 8,
                maxLiveSyncPlaybackRate: 1.15,
                maxBufferLength: 8,
                maxMaxBufferLength: 16,
                backBufferLength: 6,
                lowLatencyMode: true,
                enableWorker: false, // Critical: Disable on mobile to prevent MSE worker crashes
                manifestLoadingMaxRetry: 10,
                manifestLoadingRetryDelay: 1000,
                levelLoadingMaxRetry: 10,
                levelLoadingRetryDelay: 1000,
                fragLoadingMaxRetry: 10,
                fragLoadingRetryDelay: 500,
            });
            hls.loadSource(streamUrl);
            hls.attachMedia(video);
            hostHlsRef.current = hls;

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                video.play().catch(e => console.warn('HLS autoplay muted:', e));
            });
            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            console.warn('HLS network error, recovering...', data);
                            hls.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            console.warn('HLS media error, recovering...', data);
                            hls.recoverMediaError();
                            break;
                        default:
                            setTimeout(() => {
                                try { hls.loadSource(streamUrl); } catch(e) {}
                            }, 2000);
                            break;
                    }
                }
            });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = streamUrl;
            video.addEventListener('loadedmetadata', () => {
                video.play().catch(console.warn);
            });
        }

        return () => {
            if (hostHlsRef.current) {
                hostHlsRef.current.destroy();
                hostHlsRef.current = null;
            }
        };
    }, [room, room?.streamKey, room?.streamUrl]);

    // Unmute handler sync
    useEffect(() => {
        if (hostVideoRef.current) {
            hostVideoRef.current.muted = isSoundMuted;
            if (!isSoundMuted) {
                hostVideoRef.current.play().catch(console.warn);
            }
        }
    }, [isSoundMuted]);

    const cleanup = () => {
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(t => t.stop());
        }
        peersRef.current.forEach(pc => pc.close());
        peersRef.current.clear();
        setMediaStreams(new Map());
        iceQueuesRef.current.clear();

        if (stompClientRef.current?.connected) {
            stompClientRef.current.send(`/app/live/${roomId}/broadcast`, {}, JSON.stringify({
                type: 'PEER_LEFT', senderId: myPeerId
            }));
            stompClientRef.current.disconnect();
        }
        liveAPI.leaveRoom(roomId).catch(() => {});
    };

    const wsRetryCountRef = useRef(0);
    const MAX_WS_RETRIES = 5;

    const connectSTOMP = () => {
        if (wsRetryCountRef.current >= MAX_WS_RETRIES) return;

        const socket = new SockJS(`${API_BASE || ''}/ws/chat`);
        const client = Stomp.over(socket);
        client.debug = () => {};

        client.connect({}, () => {
            wsRetryCountRef.current = 0;
            setIsConnected(true);

            client.subscribe(`/topic/live/${roomId}/participant/${myPeerId}`, (msg) => {
                try {
                    const signal = JSON.parse(msg.body);
                    handleSignal(signal);
                } catch (e) {
                    console.error('Error handling direct signal:', e);
                }
            });

            client.subscribe(`/topic/live/${roomId}`, (msg) => {
                try {
                    const signal = JSON.parse(msg.body);
                    handleBroadcast(signal);
                } catch (e) {
                    console.error('Error handling broadcast:', e);
                }
            });

            client.send(`/app/live/${roomId}/broadcast`, {}, JSON.stringify({
                type: 'REQUEST_STREAM', senderId: myPeerId, payload: myUserName
            }));
        }, (error) => {
            setIsConnected(false);
            wsRetryCountRef.current += 1;
            if (wsRetryCountRef.current < MAX_WS_RETRIES) {
                setTimeout(connectSTOMP, 3000 * wsRetryCountRef.current);
            }
        });

        stompClientRef.current = client;
    };

    const sendSignal = (targetPeerId, type, payload) => {
        if (!stompClientRef.current?.connected) return;
        stompClientRef.current.send(`/app/live/${roomId}/signal/${targetPeerId}`, {}, JSON.stringify({
            type, senderId: myPeerId, payload
        }));
    };

    const handleBroadcast = async (signal) => {
        if (signal.type === 'ROOM_ENDED' || signal.type === 'ROOM_CLOSED') {
            setIsStreamEnded(true);
            if (hostHlsRef.current) {
                try { hostHlsRef.current.destroy(); } catch (_) {}
            }
            if (hostVideoRef.current) {
                try { hostVideoRef.current.pause(); } catch (_) {}
            }
            return;
        }

        if (signal.type === 'CO_HOST_INVITES_STATUS') {
            const isOpen = String(signal.payload || '').toUpperCase().includes('OPEN') || signal.payload === true;
            setIsCoHostInvitesOpen(isOpen);
            return;
        }

        if (signal.type === 'FLASH_SALE_STARTED') {
            try {
                const p = typeof signal.payload === 'string' ? JSON.parse(signal.payload) : signal.payload;
                const discount = Number(p?.discount || p?.discountPercent);
                if (p && !isNaN(discount) && discount > 0) {
                    const duration = p?.durationSeconds || p?.duration || 180;
                    const min = Math.floor(duration / 60);
                    const sec = duration % 60;
                    setFlashSale({
                        isActive: true,
                        discountPercent: discount,
                        timer: `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`,
                        timeRemainingSeconds: duration,
                        stockRemaining: p?.stock || p?.stockRemaining || 0,
                        ordersCount: p?.ordersCount || 0,
                        latestBuyer: p?.latestBuyer || null
                    });
                }
            } catch (e) {
                console.warn('Invalid FLASH_SALE_STARTED signal received, skipping fake sale injection:', e);
            }
            return;
        }

        if (signal.type === 'FLASH_SALE_ENDED') {
            setFlashSale(null);
            return;
        }

        if (signal.senderId === myPeerId && signal.type !== 'PEER_KICKED') return;

        if (signal.type === 'REACTION') {
            triggerHeart(null, signal.payload, false);
            return;
        }

        if (signal.type === 'CHAT_MESSAGE') {
            setMessages(prev => [...prev, {
                id: Date.now() + Math.random(),
                senderId: signal.senderId,
                senderName: signal.senderName || 'User',
                text: signal.payload,
                timestamp: new Date()
            }]);
            setTimeout(() => {
                chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
            return;
        }

        if (signal.type === 'CLEAR_CHAT') {
            setMessages([]);
            return;
        }

        if (signal.type === 'ICE_CANDIDATE') {
            const hostId = signal.senderId || (room?.hostId || 1);
            if (peersRef.current.has(hostId)) {
                const pc = peersRef.current.get(hostId);
                if (pc.remoteDescription) {
                    pc.addIceCandidate(new RTCIceCandidate(signal.payload)).catch(console.error);
                } else {
                    if (!iceQueuesRef.current.has(hostId)) iceQueuesRef.current.set(hostId, []);
                    iceQueuesRef.current.get(hostId).push(signal.payload);
                }
            }
        }

        if (signal.type === 'PEER_JOINED') {
            const parts = await liveAPI.getParticipants(roomId).catch(() => []);
            if (parts.length > 0) setParticipants(parts);
        }

        if (signal.type === 'PEER_LEFT' || signal.type === 'PEER_KICKED' || signal.type === 'PEER_BANNED') {
            if (signal.senderId === myUserId && (signal.type === 'PEER_KICKED' || signal.type === 'PEER_BANNED')) {
                setIsStreamEnded(true);
                return;
            }

            const parts = await liveAPI.getParticipants(roomId).catch(() => []);
            if (parts.length > 0) setParticipants(parts);

            if (peersRef.current.has(signal.senderId)) {
                peersRef.current.get(signal.senderId).close();
                peersRef.current.delete(signal.senderId);
            }

            setMediaStreams(prev => {
                const newMap = new Map(prev);
                newMap.delete(signal.senderId);
                return newMap;
            });
            iceQueuesRef.current.delete(signal.senderId);
        }

        if (signal.type === 'PIN_PRODUCT') {
            setPinnedProduct(signal.payload);
        }

        if (signal.type === 'UNPIN_PRODUCT') {
            setPinnedProduct(null);
        }
    };

    const handleSignal = async (signal) => {
        if (!peersRef.current.has(signal.senderId)) {
            if (signal.type === 'ICE_CANDIDATE') {
                if (!iceQueuesRef.current.has(signal.senderId)) {
                    iceQueuesRef.current.set(signal.senderId, []);
                }
                iceQueuesRef.current.get(signal.senderId).push(signal.payload);
                return;
            } else if (signal.type !== 'OFFER') {
                return;
            }
        }

        let pc = peersRef.current.get(signal.senderId);

        if (signal.type === 'JOIN_ACCEPT') {
            setRequestStatus('accepted');
            upgradeToGuest();
            return;
        }

        if (signal.type === 'JOIN_REJECT') {
            setRequestStatus('rejected');
            setTimeout(() => setRequestStatus('none'), 3000);
            return;
        }

        try {
            switch (signal.type) {
                case 'OFFER':
                    if (!pc) pc = createPeerConnection(signal.senderId);
                    await pc.setRemoteDescription(new RTCSessionDescription(signal.payload));

                    if (iceQueuesRef.current.has(signal.senderId)) {
                        for (const candidate of iceQueuesRef.current.get(signal.senderId)) {
                            await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(console.error);
                        }
                        iceQueuesRef.current.delete(signal.senderId);
                    }

                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);
                    sendSignal(signal.senderId, 'ANSWER', pc.localDescription);
                    break;
                case 'ANSWER':
                    await pc.setRemoteDescription(new RTCSessionDescription(signal.payload));
                    if (iceQueuesRef.current.has(signal.senderId)) {
                        for (const candidate of iceQueuesRef.current.get(signal.senderId)) {
                            await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(console.error);
                        }
                        iceQueuesRef.current.delete(signal.senderId);
                    }
                    break;
                case 'ICE_CANDIDATE':
                    if (pc.remoteDescription && pc.remoteDescription.type) {
                        await pc.addIceCandidate(new RTCIceCandidate(signal.payload)).catch(console.error);
                    } else {
                        if (!iceQueuesRef.current.has(signal.senderId)) {
                            iceQueuesRef.current.set(signal.senderId, []);
                        }
                        iceQueuesRef.current.get(signal.senderId).push(signal.payload);
                    }
                    break;
            }
        } catch (err) {
            console.error('WebRTC Error handling signal:', err);
        }
    };

    const createPeerConnection = (peerId) => {
        const pc = new RTCPeerConnection(ICE_SERVERS);
        peersRef.current.set(peerId, pc);

        pc.onicecandidate = (e) => {
            if (e.candidate) {
                sendSignal(peerId, 'ICE_CANDIDATE', e.candidate);
            }
        };

        pc.ontrack = (event) => {
            const stream = (event.streams && event.streams.length > 0) ? event.streams[0] : new MediaStream([event.track]);
            setMediaStreams(prev => {
                const newMap = new Map(prev);
                newMap.set(peerId, stream);
                return newMap;
            });
        };

        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));
        } else {
            pc.addTransceiver('video', { direction: 'recvonly' });
            pc.addTransceiver('audio', { direction: 'recvonly' });
        }

        return pc;
    };

    const upgradeToGuest = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 480 }, audio: true });
            localStreamRef.current = stream;
            peersRef.current.forEach(async (pc, peerId) => {
                stream.getTracks().forEach(track => pc.addTrack(track, stream));
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                sendSignal(peerId, 'OFFER', pc.localDescription);
            });
        } catch (e) {
            alert('تعذر فتح الكاميرا أو الميكروفون.');
            setRequestStatus('none');
        }
    };

    const sendChatMessage = (e) => {
        e.preventDefault();
        if (!chatInput.trim() || !stompClientRef.current?.connected) return;

        // Require real authentication to participate in live chat
        if (!isAuthenticated && !localStorage.getItem('customer_token')) {
            setIsAuthModalOpen(true);
            showToast(roomStrings.loginRequiredForChat || 'يرجى تسجيل الدخول بحسابك الحقيقي للتعليق في البث 🔒');
            return;
        }

        const text = chatInput.trim();
        stompClientRef.current.send(`/app/live/${roomId}/broadcast`, {}, JSON.stringify({
            type: 'CHAT_MESSAGE',
            senderId: myPeerId,
            senderName: myUserName,
            payload: text
        }));

        setMessages(prev => [...prev, {
            id: Date.now(),
            senderId: myPeerId,
            senderName: myUserName,
            text: text,
            timestamp: new Date()
        }]);

        setChatInput('');
        setTimeout(() => {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const host = participants.find(p => p.role === 'HOST') || participants[0];
    const hostStream = mediaStreams.get(host?.userId) || (mediaStreams.size > 0 ? Array.from(mediaStreams.values())[0] : null);
    const remoteGuestStreams = Array.from(mediaStreams.entries()).filter(([pId]) => {
        if (!host) return false;
        return String(pId) !== String(host.userId);
    });

    const getInitials = (name) => name ? name.substring(0, 2).toUpperCase() : 'LX';

    return (
        <div className="lux-live-room-wrapper" dir={rtl ? 'rtl' : 'ltr'}>
            {/* Real In-Stream Authentication Modal (Luxury Popup) */}
            {isAuthModalOpen && (
                <div dir={rtl ? 'rtl' : 'ltr'} style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,8,0.88)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                    <div style={{ background: '#111219', border: '1px solid rgba(255, 255, 255,0.4)', boxShadow: '0 24px 60px rgba(0,0,0,0.85), 0 0 35px rgba(255, 255, 255,0.18)', borderRadius: '24px', padding: '28px 22px', maxWidth: '420px', width: '100%', color: '#fff', position: 'relative', boxSizing: 'border-box' }}>
                        
                        {/* Close / Return to Home Button */}
                        <button
                            onClick={onLeave}
                            type="button"
                            style={{ position: 'absolute', top: '16px', [rtl ? 'left' : 'right']: '16px', background: 'rgba(255,255,255,0.06)', border: 'none', color: '#9ca3af', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        >
                            <X size={18} />
                        </button>

                        {/* Header with Luxury Brand Icon */}
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg, #FFFFFF, #AA771C)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(255, 255, 255,0.4)', marginBottom: '10px' }}>
                                <Sparkles size={26} color="#050508" />
                            </div>
                            <h2 style={{ fontSize: '19px', fontWeight: '800', margin: '0 0 6px 0', color: '#FFFFFF', letterSpacing: '0.3px' }}>
                                {roomStrings.loginTitle || "تسجيل الدخول للبث المباشر (LUXVONO LIVE)"}
                            </h2>
                            <p style={{ color: '#9ca3af', fontSize: '12.5px', margin: 0, lineHeight: '1.5' }}>
                                {roomStrings.loginDesc || "سجّل دخولك بحسابك الرسمي للمشاركة والتفاعل في البث المباشر والشراء الفوري."}
                            </p>
                        </div>

                        {/* Real Login Form */}
                        <form onSubmit={handleRealLogin} style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
                            {loginError && (
                                <div style={{ background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '12px', padding: '10px 14px', color: '#FCA5A5', fontSize: '12.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <AlertCircle size={16} style={{ flexShrink: 0, color: '#EF4444' }} />
                                    <span>{loginError}</span>
                                </div>
                            )}

                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#D1D5DB', marginBottom: '6px' }}>
                                    {roomStrings.emailOrPhone || "البريد الإلكتروني أو اسم المستخدم"}
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <Mail size={16} style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', [rtl ? 'right' : 'left']: '14px', color: '#9CA3AF' }} />
                                    <input
                                        type="text"
                                        value={loginEmail}
                                        onChange={(e) => setLoginEmail(e.target.value)}
                                        placeholder={roomStrings.emailPlaceholder || "name@example.com"}
                                        required
                                        style={{ width: '100%', background: '#191a24', border: '1px solid rgba(255,255,255,0.14)', color: '#fff', padding: rtl ? '12px 38px 12px 14px' : '12px 14px 12px 38px', borderRadius: '12px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#D1D5DB', marginBottom: '6px' }}>
                                    {roomStrings.password || "كلمة المرور"}
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <Lock size={16} style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', [rtl ? 'right' : 'left']: '14px', color: '#9CA3AF' }} />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={loginPassword}
                                        onChange={(e) => setLoginPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                        style={{ width: '100%', background: '#191a24', border: '1px solid rgba(255,255,255,0.14)', color: '#fff', padding: rtl ? '12px 38px 12px 38px' : '12px 38px 12px 38px', borderRadius: '12px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(p => !p)}
                                        style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', [rtl ? 'left' : 'right']: '12px', background: 'transparent', border: 'none', color: '#9CA3AF', cursor: 'pointer', padding: 0, display: 'flex' }}
                                    >
                                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            {/* Submit Real Login */}
                            <button
                                type="submit"
                                disabled={loginLoading || !loginEmail.trim() || !loginPassword}
                                style={{
                                    marginTop: '6px',
                                    padding: '13px',
                                    background: loginLoading ? 'rgba(255, 255, 255,0.4)' : 'linear-gradient(135deg, #FFFFFF, #FFFFFF)',
                                    border: 'none',
                                    color: '#050508',
                                    borderRadius: '12px',
                                    cursor: loginLoading ? 'not-allowed' : 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '14.5px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    boxShadow: '0 4px 14px rgba(255, 255, 255,0.3)',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {loginLoading ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        <span>{roomStrings.loggingIn || "جاري التحقق والدخول..."}</span>
                                    </>
                                ) : (
                                    <>
                                        <LogIn size={18} />
                                        <span>{roomStrings.loginBtn || "تسجيل الدخول والمتابعة 👑"}</span>
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Extra Options: Register & Guest View */}
                        <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '9px', textAlign: 'center' }}>
                            <div style={{ fontSize: '12.5px', color: '#9CA3AF' }}>
                                <span>{roomStrings.dontHaveAccount || "ليس لديك حساب؟"} </span>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/register?redirect=${encodeURIComponent(window.location.pathname)}`)}
                                    style={{ background: 'none', border: 'none', color: '#FFFFFF', fontWeight: 'bold', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                                >
                                    {roomStrings.createAccount || "إنشاء حساب جديد"}
                                </button>
                            </div>

                            <button
                                type="button"
                                onClick={handleContinueAsGuest}
                                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: '#D1D5DB', padding: '9px', borderRadius: '10px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', transition: 'all 0.2s' }}
                            >
                                {roomStrings.continueAsViewer || "المتابعة كزائر (مشاهدة فقط بدون تعليق)"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ambient Lighting Layer */}
            <div className="lux-live-ambient-layer">
                <div
                    className="lux-live-ambient-bg"
                    style={{ backgroundImage: `url('${room?.coverImage || "https://images.unsplash.com/photo-1542816417-0983c9c9ad53?q=80&w=1080&auto=format&fit=crop"}')` }}
                />
                <div className="lux-live-ambient-overlay" />
            </div>

            {/* Video Player & Live Canvas Layer (Supports TikTok Vertical Swipe Up/Down + Wheel) */}
            <div
                className="lux-live-video-layer"
                onClick={handleVideoClick}
                onTouchStart={handleStageTouchStart}
                onTouchMove={handleStageTouchMove}
                onTouchEnd={handleStageTouchEnd}
                onWheel={handleStageWheel}
                style={{
                    transform: swipeOffsetY !== 0 ? `translateY(${swipeOffsetY}px)` : 'translateY(0)',
                    transition: touchStartYRef.current === null ? 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)' : 'none'
                }}
            >
                {hostStream ? (
                    <RemoteVideoPlayer stream={hostStream} isMuted={isSoundMuted} />
                ) : (room?.streamKey || room?.streamUrl) ? (
                    <video
                        ref={hostVideoRef}
                        autoPlay
                        playsInline
                        webkit-playsinline="true"
                        x5-playsinline="true"
                        muted={isSoundMuted}
                        className="lux-live-video-element"
                    />
                ) : (
                    <div className="lux-live-stage-placeholder">
                        <div className="lux-live-host-avatar-ring">
                            <div className="lux-live-host-avatar">
                                {host?.avatarUrl ? (
                                    <img src={host.avatarUrl} alt="Host" />
                                ) : (
                                    <span>{getInitials(host?.userName || 'Admin')}</span>
                                )}
                            </div>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffffff', marginBottom: '6px' }}>
                            {host?.userName || 'LUXVONO Master Host'}
                        </h3>
                        <p style={{ fontSize: '12px', color: '#FFFFFF', fontWeight: 'bold', letterSpacing: '1px', textTransform: 'uppercase' }}>
                            {roomStrings.connectedLive}
                        </p>
                    </div>
                )}

                {/* Unmute Stream Banner */}
                {isSoundMuted && (
                    <div
                        className="lux-live-unmute-overlay"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsSoundMuted(false);
                            if (hostVideoRef.current) {
                                hostVideoRef.current.muted = false;
                                hostVideoRef.current.play().catch(console.warn);
                            }
                        }}
                    >
                        <VolumeX size={18} style={{ color: '#EF4444' }} />
                        {roomStrings.unmute}
                    </div>
                )}
            </div>

            {/* Floating Flying Hearts Overlay (TikTok Hearts on tap) */}
            <div className="lux-tiktok-hearts-container">
                {floatingHearts.map(h => (
                    <span
                        key={h.id}
                        className="lux-tiktok-floating-heart"
                        style={{
                            left: `${h.x}px`,
                            bottom: `${h.bottom || 90}px`,
                            '--sway-1': h.sway1,
                            '--sway-2': h.sway2,
                            '--rot-1': h.rot1,
                            '--rot-2': h.rot2,
                            fontSize: `${h.size || 28}px`
                        }}
                    >
                        {h.emoji}
                    </span>
                ))}
            </div>

            {/* In-Stream Floating Toast Notification */}
            {toastMessage && (
                <div className="lux-tiktok-toast">
                    <Sparkles size={14} style={{ color: '#FFFFFF' }} />
                    <span>{toastMessage}</span>
                </div>
            )}

            {/* Top Navigation & Status Bar */}
            <div className="lux-live-header">
                <div className="lux-live-host-pill">
                    <div className="lux-live-host-pill-avatar">
                        {getInitials(host?.userName || 'AD')}
                    </div>
                    <div className="lux-live-host-pill-info">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span className="lux-live-host-name">{host?.userName || 'Admin Host'}</span>
                            <button
                                type="button"
                                className={`lux-live-follow-btn ${isFollowing ? 'following' : ''}`}
                                onClick={handleToggleFollow}
                            >
                                {isFollowing ? (
                                    <>
                                        <CheckCircle2 size={11} />
                                        <span>{roomStrings.following || '✓ متابع'}</span>
                                    </>
                                ) : (
                                    <>
                                        <Plus size={11} />
                                        <span>{roomStrings.follow || '+ متابعة'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                        <div className="lux-live-status-row">
                            <span className="lux-live-dot-ping" />
                            <span>{roomStrings.liveDot} • <Users size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> {Math.max(1, participants.length)}</span>
                        </div>
                    </div>
                </div>

                <button className="lux-live-close-btn" onClick={onLeave} title={roomStrings.leave}>
                    <X size={18} />
                </button>
            </div>

            {/* TikTok Shop Live Flash Sale & Live Order Ticker Banner (Real data only, no mock/fake) */}
            {flashSale && flashSale.isActive && (
                <div style={{
                    position: 'absolute',
                    top: '72px',
                    left: '16px',
                    right: '16px',
                    zIndex: 20,
                    background: 'linear-gradient(135deg, rgba(20, 15, 5, 0.94) 0%, rgba(10, 10, 15, 0.96) 100%)',
                    backdropFilter: 'blur(12px)',
                    border: '1.5px solid rgba(245, 158, 11, 0.65)',
                    borderRadius: '16px',
                    padding: '8px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 8px 32px rgba(245, 158, 11, 0.25)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'linear-gradient(135deg, #EF4444, #F59E0B)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>
                            ⚡
                        </div>
                        <div>
                            <div style={{ fontSize: '11px', color: '#FDE047', fontWeight: 'bold' }}>
                                صفقة فلاش سيل خصم {flashSale.discountPercent}% 🏷️
                            </div>
                            <div style={{ fontSize: '10px', color: '#EF4444', fontWeight: '800', marginTop: '1px' }}>
                                ⏳ ينتهي العرض: {flashSale.timer}
                            </div>
                        </div>
                    </div>

                    <div style={{ textAlign: rtl ? 'left' : 'right' }}>
                        {flashSale.stockRemaining > 0 && (
                            <div style={{ background: 'rgba(239, 68, 68, 0.25)', border: '1px solid #EF4444', color: '#FCA5A5', fontSize: '10px', fontWeight: '900', padding: '2px 8px', borderRadius: '10px', display: 'inline-block' }}>
                                🔥 متبقي {flashSale.stockRemaining} قطع فقط!
                            </div>
                        )}
                        {flashSale.latestBuyer && (
                            <div style={{ fontSize: '9.5px', color: '#6EE7B7', fontWeight: 'bold', marginTop: '2px', maxWidth: '140px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {flashSale.latestBuyer}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Real Remote Co-Host / Guest Video Window (Only rendered if host actually approved a real guest via WebRTC) */}
            {remoteGuestStreams.map(([guestPeerId, guestStream]) => {
                const guestParticipant = participants.find(p => String(p.userId) === String(guestPeerId));
                return (
                    <div
                        key={guestPeerId}
                        style={{
                            position: 'absolute',
                            top: (flashSale && flashSale.isActive) ? '145px' : '78px',
                            left: '16px',
                            right: 'auto',
                            width: '110px',
                            height: '160px',
                            zIndex: 25,
                            borderRadius: '16px',
                            overflow: 'hidden',
                            background: '#0e1017',
                            border: '2px solid rgba(255, 255, 255, 0.8)',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <RemoteVideoPlayer stream={guestStream} isMuted={false} />
                        <div style={{
                            position: 'absolute',
                            bottom: '6px',
                            left: '6px',
                            right: '6px',
                            background: 'rgba(0,0,0,0.7)',
                            padding: '2px 6px',
                            borderRadius: '6px',
                            fontSize: '9px',
                            color: '#fff',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {guestParticipant?.userName || 'Guest 🎙️'}
                        </div>
                    </div>
                );
            })}

            {/* Local Guest Self-View Window (if viewer themselves was accepted as guest) */}
            {localStreamRef.current && (
                <div
                    style={{
                        position: 'absolute',
                        top: (flashSale && flashSale.isActive) ? '145px' : '78px',
                        left: '16px',
                        right: 'auto',
                        width: '110px',
                        height: '160px',
                        zIndex: 25,
                        borderRadius: '16px',
                        overflow: 'hidden',
                        background: '#0e1017',
                        border: '2px solid rgba(16, 185, 129, 0.8)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.6)'
                    }}
                >
                    <RemoteVideoPlayer stream={localStreamRef.current} isMuted={true} />
                    <div style={{
                        position: 'absolute',
                        bottom: '6px',
                        left: '6px',
                        right: '6px',
                        background: 'rgba(0,0,0,0.7)',
                        padding: '2px 6px',
                        borderRadius: '6px',
                        fontSize: '9px',
                        color: '#fff',
                        textAlign: 'center'
                    }}>
                        {myUserName} ({roomStrings.you || 'أنت'})
                    </div>
                </div>
            )}

            {/* TikTok Vertical Action Rail (Right / Thumb-friendly) */}
            <div className="lux-tiktok-rail">
                {/* TikTok Vertical Live Stream Switcher (Swipe Up / Down between live hosts) */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                        background: 'rgba(15, 17, 26, 0.72)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        borderRadius: '22px',
                        padding: '5px 4px',
                        marginBottom: '4px',
                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.45)'
                    }}
                    title={currentLang === 'ar' ? 'مرر للأعلى أو الأسفل للتنقل بين البثوث المباشرة' : 'Swipe up or down to switch live streams'}
                >
                    <button
                        type="button"
                        onClick={() => triggerVerticalRoomSwipe('prev')}
                        aria-label="البث السابق"
                        style={{
                            width: '32px',
                            height: '28px',
                            borderRadius: '14px',
                            border: 'none',
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: '#FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <ChevronUp size={17} />
                    </button>
                    <span
                        style={{
                            fontSize: '9.5px',
                            fontWeight: '800',
                            color: '#FFFFFF',
                            padding: '1px 5px',
                            letterSpacing: '0.3px',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {`${displayRoomIndex + 1}/${Math.max(1, effectiveRooms.length)}`}
                    </span>
                    <button
                        type="button"
                        onClick={() => triggerVerticalRoomSwipe('next')}
                        aria-label="البث التالي"
                        style={{
                            width: '32px',
                            height: '28px',
                            borderRadius: '14px',
                            border: 'none',
                            background: 'rgba(254, 44, 85, 0.85)',
                            color: '#FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <ChevronDown size={17} />
                    </button>
                </div>

                {/* Like Button with animated counter */}
                <button
                    type="button"
                    className="lux-tiktok-rail-btn like-btn"
                    onClick={(e) => {
                        triggerHeart(e, '💖', true);
                    }}
                    title={roomStrings.like || "إعجاب"}
                >
                    <div className="lux-tiktok-rail-icon">
                        <Heart size={22} fill="#F43F5E" />
                    </div>
                    <span className="lux-tiktok-rail-label">{likesCount.toLocaleString()}</span>
                </button>

                {/* Royal Crown Gift 👑 */}
                <button
                    type="button"
                    className="lux-tiktok-rail-btn"
                    onClick={() => {
                        triggerHeart(null, '👑', true);
                        showToast(roomStrings.sentCrown || '👑 أرسلت تاج الملوك للبث المباشر!');
                    }}
                    title={roomStrings.crown || "تاج"}
                >
                    <div className="lux-tiktok-rail-icon" style={{ borderColor: 'rgba(255, 255, 255, 0.4)' }}>
                        <span>👑</span>
                    </div>
                    <span className="lux-tiktok-rail-label">{roomStrings.crown || "تاج"}</span>
                </button>

                {/* Luxury Diamond Gift 💎 */}
                <button
                    type="button"
                    className="lux-tiktok-rail-btn"
                    onClick={() => {
                        triggerHeart(null, '💎', true);
                        showToast(roomStrings.sentDiamond || '💎 أرسلت ماسة فاخرة لمقدم البث!');
                    }}
                    title={roomStrings.gift || "إهداء"}
                >
                    <div className="lux-tiktok-rail-icon" style={{ borderColor: 'rgba(56, 189, 248, 0.4)' }}>
                        <span>💎</span>
                    </div>
                    <span className="lux-tiktok-rail-label">{roomStrings.gift || "إهداء"}</span>
                </button>

                {/* TikTok Shop Bag / Yellow Cart */}
                <button
                    type="button"
                    className="lux-tiktok-rail-btn"
                    onClick={() => setIsCatalogDrawerOpen(true)}
                    title={roomStrings.bag || "شنطة البث"}
                >
                    <div className="lux-tiktok-rail-icon" style={{ background: 'linear-gradient(135deg, rgba(255, 255, 255,0.25), rgba(20,20,28,0.85))', borderColor: '#FFFFFF' }}>
                        <ShoppingBag size={20} style={{ color: '#FFFFFF' }} />
                        {catalogProducts.length > 0 && (
                            <span className="lux-tiktok-rail-badge">{catalogProducts.length}</span>
                        )}
                    </div>
                    <span className="lux-tiktok-rail-label" style={{ color: '#FFFFFF' }}>{roomStrings.bag || "شنطة البث"}</span>
                </button>

                {/* Share Stream */}
                <button
                    type="button"
                    className="lux-tiktok-rail-btn"
                    onClick={handleShare}
                    title={roomStrings.share || "مشاركة"}
                >
                    <div className="lux-tiktok-rail-icon">
                        <Share2 size={18} />
                    </div>
                    <span className="lux-tiktok-rail-label">{roomStrings.share || "مشاركة"}</span>
                </button>

                {/* Audio Mute/Unmute */}
                <button
                    type="button"
                    className="lux-tiktok-rail-btn"
                    onClick={() => {
                        setIsSoundMuted(prev => !prev);
                        if (hostVideoRef.current) hostVideoRef.current.muted = !isSoundMuted;
                    }}
                    title={roomStrings.sound || "الصوت"}
                >
                    <div className="lux-tiktok-rail-icon">
                        {isSoundMuted ? <VolumeX size={18} style={{ color: '#EF4444' }} /> : <Volume2 size={18} style={{ color: '#10B981' }} />}
                    </div>
                    <span className="lux-tiktok-rail-label">{isSoundMuted ? "صامت" : "صوت"}</span>
                </button>
            </div>

            {/* Bottom Section: Pinned Products + Chat Stream */}
            <div className="lux-live-content-bottom">
                {/* TikTok Shop Pinned Product Card */}
                {pinnedProduct && (
                    <div className="lux-live-pinned-product">
                        <div className="lux-live-pinned-details" onClick={() => navigate(`/product/${pinnedProduct.id}`)} style={{ cursor: 'pointer' }}>
                            <div style={{ position: 'relative' }}>
                                <img
                                    src={pinnedProduct.imageUrl || pinnedProduct.image || "https://images.unsplash.com/photo-1542816417-0983c9c9ad53?q=80&w=200&auto=format&fit=crop"}
                                    alt={pinnedProduct.name}
                                    className="lux-live-pinned-thumb"
                                />
                                <span style={{ position: 'absolute', top: 2, left: 2, background: '#EF4444', color: '#fff', fontSize: '8px', fontWeight: 800, padding: '1px 4px', borderRadius: 4 }}>
                                    LIVE ⚡
                                </span>
                            </div>
                            <div className="lux-live-pinned-info">
                                <div className="lux-live-pinned-tag">{roomStrings.pinnedExclusive || '★ معروض الآن في البث ⚡'}</div>
                                <div className="lux-live-pinned-title">{pinnedProduct.name}</div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                    <span className="lux-live-pinned-price">${Number(pinnedProduct.price || 0).toFixed(2)}</span>
                                    {pinnedProduct.originalPrice && (
                                        <span style={{ fontSize: '11px', color: '#6b7280', textDecoration: 'line-through' }}>
                                            ${Number(pinnedProduct.originalPrice).toFixed(2)}
                                        </span>
                                    )}
                                    {pinnedProduct.discountPercent && (
                                        <span style={{ fontSize: '10px', color: '#10B981', fontWeight: 800, background: 'rgba(16,185,129,0.15)', padding: '1px 5px', borderRadius: '4px' }}>
                                            -{pinnedProduct.discountPercent}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="lux-live-pinned-actions">
                            <button
                                type="button"
                                onClick={() => handleAddToCart(pinnedProduct)}
                                className="lux-live-btn-glass"
                            >
                                <ShoppingBag size={13} style={{ display: 'inline', verticalAlign: 'middle', marginInlineEnd: '4px' }} />
                                {roomStrings.addToCart || 'إضافة للسلة 🛒'}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleBuyNow(pinnedProduct)}
                                className="lux-live-btn-gold"
                            >
                                <Zap size={13} style={{ display: 'inline', verticalAlign: 'middle', marginInlineEnd: '4px' }} />
                                {roomStrings.instantBuy || 'شراء فوري ⚡'}
                            </button>
                        </div>
                    </div>
                )}

                {/* TikTok / IG Live Style Chat Bubble Stream */}
                <div className="lux-live-chat-stream">
                    {messages.map((msg, idx) => {
                        const isHost = host && String(host.userId) === String(msg.senderId);
                        const isMe = String(msg.senderId) === String(myPeerId);
                        return (
                            <div key={msg.id || idx} className="lux-live-chat-bubble">
                                <span className={`lux-live-chat-sender ${isHost ? 'host' : (isMe ? 'me' : '')}`}>
                                    {isHost ? '👑 ' : ''}{isMe ? roomStrings.you : msg.senderName}:
                                </span>
                                <span className="lux-live-chat-text">{msg.text}</span>
                            </div>
                        );
                    })}
                    <div ref={chatEndRef} />
                </div>
            </div>

            {/* Action Bar (Input & Controls & Quick Emojis) */}
            <div className="lux-live-bottom-bar">
                <form onSubmit={sendChatMessage} className="lux-live-input-box" onClick={(e) => e.stopPropagation()}>
                    <MessageCircle size={16} style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
                    <input
                        type="text"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onFocus={() => {
                            if (!isAuthenticated && !localStorage.getItem('customer_token')) {
                                setIsAuthModalOpen(true);
                            }
                        }}
                        placeholder={(!isAuthenticated && !localStorage.getItem('customer_token')) ? (roomStrings.loginRequiredForChat || 'سجّل دخولك بحسابك للتعليق...') : roomStrings.chatPlaceholder}
                        className="lux-live-input-field"
                    />
                    <button
                        type="submit"
                        disabled={!chatInput.trim()}
                        className="lux-live-send-btn"
                        title={roomStrings.send}
                    >
                        <Send size={16} />
                    </button>
                </form>

                {/* Quick Reaction Emojis Toolbar */}
                <div className="lux-tiktok-quick-emojis">
                    {['❤️', '💖', '💎', '👑', '🔥', '✨', '🌹'].map((emoji, i) => (
                        <button
                            key={i}
                            type="button"
                            className="lux-tiktok-quick-emoji-btn"
                            onClick={() => triggerHeart(null, emoji, true)}
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
            </div>

            {/* Slide-Up Live Products Bag (TikTok Shop Drawer) */}
            {isCatalogDrawerOpen && (
                <>
                    <div className="lux-tiktok-drawer-backdrop" onClick={() => setIsCatalogDrawerOpen(false)} />
                    <div className="lux-tiktok-drawer" dir={rtl ? 'rtl' : 'ltr'}>
                        <div className="lux-tiktok-drawer-header">
                            <div className="lux-tiktok-drawer-title-group">
                                <ShoppingBag size={18} style={{ color: '#FFFFFF' }} />
                                <span className="lux-tiktok-drawer-title">
                                    {roomStrings.bagTitle || 'حقيبة منتجات البث (TikTok Shop)'}
                                </span>
                                <span className="lux-tiktok-drawer-count">{catalogProducts.length}</span>
                            </div>
                            <button
                                type="button"
                                className="lux-tiktok-drawer-close"
                                onClick={() => setIsCatalogDrawerOpen(false)}
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <div className="lux-tiktok-drawer-list">
                            {catalogProducts.map((prod, idx) => {
                                const isPinned = pinnedProduct && String(pinnedProduct.id) === String(prod.id);
                                return (
                                    <div key={prod.id || idx} className={`lux-tiktok-drawer-item ${isPinned ? 'pinned' : ''}`}>
                                        <div className="lux-tiktok-drawer-item-img-box" onClick={() => navigate(`/product/${prod.id}`)} style={{ cursor: 'pointer' }}>
                                            <img
                                                src={prod.imageUrl || prod.image || "https://images.unsplash.com/photo-1542816417-0983c9c9ad53?q=80&w=200&auto=format&fit=crop"}
                                                alt={prod.name}
                                                className="lux-tiktok-drawer-item-img"
                                            />
                                            {isPinned ? (
                                                <span className="lux-tiktok-drawer-item-tag">معروض 👑</span>
                                            ) : (
                                                <span className="lux-tiktok-drawer-item-tag" style={{ background: '#38BDF8' }}>LIVE</span>
                                            )}
                                        </div>
                                        <div className="lux-tiktok-drawer-item-body">
                                            <div onClick={() => navigate(`/product/${prod.id}`)} style={{ cursor: 'pointer' }}>
                                                <div className="lux-tiktok-drawer-item-title">{prod.name}</div>
                                                <div className="lux-tiktok-drawer-item-price-row">
                                                    <span className="lux-tiktok-drawer-item-price">${Number(prod.price || 0).toFixed(2)}</span>
                                                    {prod.originalPrice && (
                                                        <span className="lux-tiktok-drawer-item-old-price">${Number(prod.originalPrice).toFixed(2)}</span>
                                                    )}
                                                    {prod.discountPercent && (
                                                        <span className="lux-tiktok-drawer-item-discount">-{prod.discountPercent}%</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="lux-tiktok-drawer-item-actions">
                                                <button
                                                    type="button"
                                                    className="lux-tiktok-drawer-btn-cart"
                                                    onClick={() => handleAddToCart(prod)}
                                                >
                                                    <ShoppingBag size={12} />
                                                    <span>{roomStrings.addToCart || 'السلة 🛒'}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="lux-tiktok-drawer-btn-buy"
                                                    onClick={() => handleBuyNow(prod)}
                                                >
                                                    <Zap size={12} />
                                                    <span>{roomStrings.instantBuy || 'شراء فوري ⚡'}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            {/* Stream Ended Luxury Reminder Overlay */}
            {isStreamEnded && (
                <div className="lux-live-ended-overlay">
                    <div className="lux-live-ended-card">
                        <div className="lux-live-ended-badge">
                            <Sparkles size={14} style={{ color: '#FFFFFF' }} />
                            <span>LUXVONO SOVEREIGN LIVE</span>
                        </div>

                        <div className="lux-live-ended-icon-ring">
                            <Bell size={42} className="lux-live-ended-bell" />
                        </div>

                        <h2 className="lux-live-ended-title">
                            {roomStrings.streamEndedTitle || '👑 تم إغلاق البث المباشر'}
                        </h2>

                        <p className="lux-live-ended-subtitle">
                            {roomStrings.streamEndedSubtitle || 'أغلق البائع جلسة البث المباشر. اضغط على زر التذكير لتصلك إشعارات وتنبيهات حصرية فور بدء البث القادم!'}
                        </p>

                        <button
                            type="button"
                            onClick={handleSetReminder}
                            disabled={reminderSubmitting}
                            className={`lux-live-remind-btn ${isReminderSet ? 'active' : ''}`}
                        >
                            {reminderSubmitting ? (
                                <>
                                    <Loader2 size={20} className="animate-spin" />
                                    <span>{roomStrings.reminderSubmitting || 'جاري تفعيل التذكير...'}</span>
                                </>
                            ) : isReminderSet ? (
                                <>
                                    <CheckCircle2 size={22} style={{ color: '#10B981' }} />
                                    <span>{roomStrings.reminderActive || '✓ تم تفعيل التذكير بنجاح! سنقوم بإشعارك فور انطلاق البث القادم 🔔'}</span>
                                </>
                            ) : (
                                <>
                                    <Bell size={22} className="lux-bell-wiggle" />
                                    <span>{roomStrings.remindMeBtn || '🔔 أغلق البث - اضغط للتذكير'}</span>
                                </>
                            )}
                        </button>

                        {isReminderSet && (
                            <div className="lux-live-reminder-status-toast">
                                <span>🔔 تم تسجيلك بنجاح! ستتلقى إشعاراً فورياً على متصفحك وحسابك فور بدء البث القادم.</span>
                            </div>
                        )}

                        <div className="lux-live-ended-actions">
                            <button
                                type="button"
                                onClick={() => navigate('/stores')}
                                className="lux-live-btn-glass"
                            >
                                <Store size={16} />
                                <span>{roomStrings.browseStore || 'تصفح المتجر'}</span>
                            </button>

                            <button
                                type="button"
                                onClick={onLeave}
                                className="lux-live-btn-gold"
                            >
                                <ArrowLeft size={16} />
                                <span>{roomStrings.backToHome || 'العودة للرئيسية'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
